import React, { useMemo, useState } from 'react';
import { ActionIcon, Checkbox, Divider, Group, NumberInput, Popover, Stack, Text, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconArrowsSort, IconFilter, IconSearch, IconSortAscending, IconSortDescending } from '@tabler/icons-react';
import { useGetChecklistMilestonesQuery } from '@repo/services';
import {deepCompareArrays, useChecklisMilestones} from "@repo/utils";

/**
 * Suffixes appended to a date column's base filter key to store the two
 * range-style date filters alongside its existing year-checkbox filter. Kept
 * as a `string[]` (epoch-ms strings / a single day count) so they ride the
 * exact same preset persistence + reset plumbing as every other filter value,
 * with no change to the `handleFilter(column, string[])` contract. The base
 * column's year-checkbox filter (`columnName`) is untouched by these.
 */
export const DATE_RANGE_FILTER_SUFFIX = '__dateRange';
/**
 * Suffix for the "last N days" filter key, combined with a column's base name to store N-day range filters in the preset.
 */
export const DATE_LASTDAYS_FILTER_SUFFIX = '__lastDays';

interface TableSearchSortFilterProps {
    search: boolean;
    sort: boolean;
    filter: boolean;
    columnName: string;
    searchColumn?: string;
    searchQuery?: string;
    sortOrders?: ('asc'|'desc'|'none')[];
    sortColumns?: string[];
    handleSearch?: (val:string) => void;
    handleSort?: (column:string,order:'asc'|'desc'|'none') => void;
    filterOptions?: string[] | undefined;
    handleFilter?: (column:any, selectedValues:any) => void;
    selectedFilters?: string[];
    /** Optional count per option value (case-insensitive key match). Shown as a dimmed number. */
    filterCounts?: Record<string, number>;
    /**
     * Opt-in for date columns: renders a date-range picker and a "Last N days"
     * input beneath the existing year checkboxes in the same filter popover.
     * Values are written via `handleFilter` under the derived keys
     * `columnName + DATE_RANGE_FILTER_SUFFIX` / `+ DATE_LASTDAYS_FILTER_SUFFIX`.
     */
    dateFilter?: boolean;
    /** Current [startMs, endMs] date-range filter for this column (as stored `string[]`), if any. */
    dateRangeFilter?: string[];
    /** Current [days] "last N days" filter for this column (as stored `string[]`), if any. */
    lastDaysFilter?: string[];
}

/**
 * Renders a trio of search, sort, and filter (enum/date) controls for a table column, with optional date-range and "last N days" filters.
 */
export const TableSearchSortFilter = ({
      search, sort, filter, columnName, searchColumn = "", searchQuery = "", sortOrders = [], sortColumns = [],
      handleSearch = () => {},handleSort = () => {}, filterOptions = undefined, handleFilter = () => {}, selectedFilters = [],
      filterCounts = {}, dateFilter = false, dateRangeFilter = [], lastDaysFilter = [],
  }:TableSearchSortFilterProps) => {
    const { data: checklistMilestones = [] } = useGetChecklistMilestonesQuery(null);
    const {sortMilestoneOptions} = useChecklisMilestones();
    const [hoveredOption, setHoveredOption] = useState<string | null>(null);

    const handleFilterCheck = (val:string, isAdd:boolean) => {
        if (val) {
            const lowerCaseVal = val?.toLowerCase();
            const filters = selectedFilters ? ([...selectedFilters as string []]?.map((f) => f?.toLowerCase())) : [] as string[] ;
            if (isAdd && filters) {
                filters.push(lowerCaseVal);
            } else {
                const index = filters?.indexOf(lowerCaseVal);
                if (index !== -1) filters?.splice(index, 1);
            }
            if (handleFilter) {
                handleFilter(columnName, filters);
            }
        }
    }

    const handleOnlyClick = (val: string) => {
        if (handleFilter) {
            handleFilter(columnName, [val.toLowerCase()]);
        }
    };

    // ─── Date-range / "last N days" filters (opt-in via `dateFilter`) ──────────
    // Stored under derived keys so they coexist with the year checkboxes above
    // without touching this column's own `string[]` year filter.
    const dateRangeKey = columnName + DATE_RANGE_FILTER_SUFFIX;
    const lastDaysKey = columnName + DATE_LASTDAYS_FILTER_SUFFIX;

    const rangeStartMs = dateRangeFilter[0] && dateRangeFilter[0].split(':')[0] ? Number(dateRangeFilter[0].split(':')[0]) : undefined;
    const rangeEndMs = dateRangeFilter[0] && dateRangeFilter[0].split(':')[1] ? Number(dateRangeFilter[0].split(':')[1]) : undefined;
    const lastDaysValue = lastDaysFilter[0] ? Number(lastDaysFilter[0]) : undefined;

    const toDateString = (ms: number | undefined): string | null => {
        // Format from LOCAL date parts (not toISOString, which is UTC) so the
        // picker redisplays exactly the day the user picked — `fromDateString`
        // builds ms from local midnight, so both ends must agree on local time.
        if (ms == null || Number.isNaN(ms)) return null;
        const d = new Date(ms);
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };
    const fromDateString = (value: string | null): number | undefined =>
        value ? new Date(`${value}T00:00:00`).getTime() : undefined;

    const handleDateRangeChange = ([start, end]: [string | null, string | null]) => {
        const startMs = fromDateString(start);
        const endMs = fromDateString(end);
        // Encode both bounds in one non-empty, lowercase-safe token ("start:end",
        // either side may be blank) so it survives the preset store's
        // `filter(Boolean)` + `toLowerCase()` normalization intact.
        const token = startMs != null || endMs != null
            ? `${startMs != null ? startMs : ''}:${endMs != null ? endMs : ''}`
            : '';
        handleFilter(dateRangeKey, token ? [token] : []);
    };

    const handleLastDaysChange = (val: number | string) => {
        const n = typeof val === 'number' ? val : Number(val);
        handleFilter(lastDaysKey, val === '' || Number.isNaN(n) || n <= 0 ? [] : [String(n)]);
    };

    const SortIcon = ({iconSize, column}:{iconSize:string, column:string}) => {
        const index = sortColumns?.indexOf(column);
        let sortOrder = 'none'
        if (sortOrders && index !== -1) {
          sortOrder = sortOrders[index] || 'none';
        }
        const isSortedByThisColumn = index !== -1;
        return (
            <ActionIcon
                variant='default'
                size={iconSize}
                aria-label='Gallery'
                style={{border:'none'}}
            >
                {sortOrder === 'none' && (
                    <Tooltip label='Sort Ascending'>
                        <IconArrowsSort onClick={() => handleSort ? handleSort(column, 'asc') : {}} />
                    </Tooltip>
                )}
                {sortOrder === 'asc' && (
                    <Tooltip label='Sort Descending'>
                        <IconSortDescending color={isSortedByThisColumn ? 'blue' : 'black'} onClick={() => handleSort ? handleSort(column, 'desc') : {}} />
                    </Tooltip>
                )}
                {sortOrder === 'desc' && (
                    <Tooltip label='Sort Default'>
                        <IconSortAscending color={isSortedByThisColumn ? 'blue' : 'black'} onClick={() => handleSort ? handleSort(column, 'none') : {}} />
                    </Tooltip>
                )}
            </ActionIcon>
        );
    }

    const isFiltered = useMemo(() => {
        if (dateFilter && (dateRangeFilter.length > 0 || lastDaysFilter.length > 0)) return true;
        if (filterOptions) {
            const compareFilterData = deepCompareArrays(filterOptions, selectedFilters);
            if (!compareFilterData) return true;
            return false;
        }
    },[filterOptions, selectedFilters, dateFilter, dateRangeFilter, lastDaysFilter])

    function sortMilestonesBySequence(arr: string[]) {
        const sequenceMap = new Map<string, number>();
        checklistMilestones.forEach(obj => {
            sequenceMap.set(obj.value, obj.sequence);
        });

        return arr.sort((a, b) => {
            const sequenceA = sequenceMap.get(a) ?? Infinity;
            const sequenceB = sequenceMap.get(b) ?? Infinity;
            return sequenceA - sequenceB;
        });
    }


    return (
      <ActionIcon.Group style={{ gap: '5px' }}>
        {sort && <SortIcon iconSize={'15'} column={columnName} />}
        {search && (
          <Popover width={200} withArrow>
            <Popover.Target>
              <ActionIcon variant='default' size='15' aria-label='Gallery' style={{ border: 'none' }}>
                <IconSearch
                  color={
                    searchQuery && searchColumn === columnName
                      ? 'var(--mantine-color-blue-filled)'
                      : 'var(--mantine-color-text)'
                  }
                />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
              <TextInput
                type='text'
                placeholder='Search by name...'
                value={searchQuery}
                onChange={(e) => (handleSearch ? handleSearch(e.target.value) : {})}
              />
            </Popover.Dropdown>
          </Popover>
        )}
        {filter && (
          <Popover width={220} withArrow>
            <Popover.Target>
              <ActionIcon variant='default' size='15' aria-label='Gallery' style={{ border: 'none' }}>
                <IconFilter
                  color={isFiltered ? 'var(--mantine-color-blue-filled)' : 'var(--mantine-color-text)'}
                />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown style={{ padding: 8 }}>
              <Stack gap={4}>
                {/* Scroll/clip only the (potentially long) checkbox list — NOT
                    the whole dropdown — so the date section's non-portaled
                    calendar below can overflow freely instead of being clipped
                    by an `overflow: auto` ancestor. */}
                <div style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <UnstyledButton
                  onClick={() => handleFilter(columnName, [])}
                  style={{ alignSelf: 'flex-end' }}
                >
                  <Text fz='xs' c='dimmed' td='underline'>Reset</Text>
                </UnstyledButton>
                {filterOptions
                  ? sortMilestoneOptions(filterOptions as string[]).map((option) => {
                      if (option && selectedFilters) {
                        const lowerCaseFilters = (selectedFilters as string[])?.map((f) => f?.toLowerCase());
                        const check = lowerCaseFilters.includes(option?.toLowerCase());
                        const label = checklistMilestones?.find((m) => m?.value === option)?.name || option;
                        const count = filterCounts[option] ?? filterCounts[option.toLowerCase()];
                        const isHovered = hoveredOption === option;
                        return (
                          <div
                            key={option}
                            onMouseEnter={() => setHoveredOption(option)}
                            onMouseLeave={() => setHoveredOption(null)}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}
                          >
                            <Checkbox
                              checked={check}
                              label={
                                <Text fz='sm' span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {label}
                                </Text>
                              }
                              onChange={(event) => handleFilterCheck(option, event.currentTarget.checked)}
                              styles={{ root: { flex: 1, minWidth: 0 }, label: { overflow: 'hidden' } }}
                            />
                            <UnstyledButton
                              onClick={(e) => { e.stopPropagation(); handleOnlyClick(option); }}
                              style={{
                                opacity: isHovered ? 1 : 0,
                                pointerEvents: isHovered ? 'auto' : 'none',
                                flexShrink: 0,
                                transition: 'opacity 120ms',
                              }}
                            >
                              <Text fz='xs' c='blue' style={{ whiteSpace: 'nowrap' }}>only</Text>
                            </UnstyledButton>
                            {count !== undefined && (
                              <Text fz='xs' c='dimmed' style={{ flexShrink: 0, minWidth: 20, textAlign: 'right' }}>
                                {count}
                              </Text>
                            )}
                          </div>
                        );
                      }
                    })
                  : undefined}
                </div>
                {dateFilter && (
                  <>
                    <Divider my={4} label='Date range' labelPosition='center' />
                    <DatePickerInput
                      type='range'
                      size='xs'
                      placeholder='Pick dates'
                      value={[toDateString(rangeStartMs), toDateString(rangeEndMs)]}
                      onChange={handleDateRangeChange as any}
                      clearable
                      allowSingleDateInRange
                      // Render the calendar inside this filter popover's DOM (not
                      // portaled to body) so clicks on the calendar's month-nav
                      // buttons aren't treated as "outside" the outer Popover and
                      // don't dismiss it (rapid prev/next clicks were closing it).
                      popoverProps={{ withinPortal: false }}
                    />
                    <NumberInput
                      size='xs'
                      label='Last N days'
                      placeholder='e.g. 30'
                      min={1}
                      value={lastDaysValue ?? ''}
                      onChange={handleLastDaysChange}
                    />
                    {(rangeStartMs != null || rangeEndMs != null || lastDaysValue != null) && (
                      <UnstyledButton
                        onClick={() => { handleFilter(dateRangeKey, []); handleFilter(lastDaysKey, []); }}
                        style={{ alignSelf: 'flex-end' }}
                      >
                        <Text fz='xs' c='dimmed' td='underline'>Clear dates</Text>
                      </UnstyledButton>
                    )}
                  </>
                )}
              </Stack>
            </Popover.Dropdown>
          </Popover>
        )}
      </ActionIcon.Group>
    );
};

export default TableSearchSortFilter;
